import { useEffect, useMemo } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import useEnableFastifyApi from './useEnableFastifyApi';
import { FLIGHT_PROFILE } from '../queries';
import restRequest from './restClient';
import useAsyncCall from './useAsyncCall';

function toGraphqlShape(bffResponse) {
  return {
    currentBooking: bffResponse?.currentBooking?.booking,
    flightProfile: bffResponse?.flightProfile,
    airports: bffResponse?.airports,
  };
}

function fetchFullProfile({ ignoreCache } = {}) {
  return restRequest({ path: '/air-api/v1/profile/full', params: { ignoreCache } }).then(
    toGraphqlShape,
  );
}

// Mirrors restClient's normalized shape: consumers read graphQLErrors to branch on upstream codes,
// and an undefined one would throw where a plain miss should simply not match.
function buildEmptyProfileError(reason) {
  const error = new Error(`profile/full returned ${reason}`);
  error.graphQLErrors = [];
  error.networkError = null;
  return error;
}

/**
 * A missing flightProfile is a FAILURE, not a partial success — even when currentBooking arrived.
 *
 * `/profile/full` composes its three sections with `Promise.allSettled` and turns a rejected one
 * into `null` (the BFF's unwrap-settled.ts), so an Air Service outage is served as HTTP 200 with
 * `flightProfile: null`. This predicate used to require BOTH sections to be missing, so that
 * response read as success: the departure results page got a profile-shaped object with nothing
 * in it, had no error to branch on, and rendered blank — no message, no retry, no way out.
 *
 * The section is the entire point of the call, so its absence is the error. When there is no
 * token at all both sections come back null and this still matches, as before.
 */
function missingProfileReason(data) {
  if (!data) return undefined;
  if (!data.currentBooking && !data.flightProfile) return 'no currentBooking and no flightProfile';
  if (!data.flightProfile) return 'no flightProfile';
  return undefined;
}

export default function useFlightProfile(options) {
  const enableFastifyApi = useEnableFastifyApi();

  const graphqlResult = useLazyQuery(FLIGHT_PROFILE, options);
  // Hook-level options.variables (Apollo's legacy pattern, used by FormReservation/Manage) merge
  // under call-time variables INSIDE the asyncFn — not in a per-render wrapper — so the returned
  // execute keeps useAsyncCall's stable identity. Components put these execute functions in
  // useEffect dependency arrays (Apollo's are stable); a fresh closure per render caused an
  // infinite re-fetch loop. useAsyncCall reads the asyncFn through a ref, so this closure still
  // sees the current options every call.
  const [restExecute, restState] = useAsyncCall(
    ({ variables } = {}) => fetchFullProfile({ ...options?.variables, ...variables }),
    options,
  );

  const missingReason = missingProfileReason(restState.data);
  const emptyProfile = Boolean(missingReason);
  const emptyProfileError = useMemo(
    () => (missingReason ? buildEmptyProfileError(missingReason) : undefined),
    [missingReason],
  );

  if (enableFastifyApi) {
    if (emptyProfile) {
      return [
        restExecute,
        { ...restState, data: undefined, error: restState.error ?? emptyProfileError },
      ];
    }
    return [restExecute, restState];
  }
  return graphqlResult;
}

export function useFlightProfileQuery(options = {}) {
  const enableFastifyApi = useEnableFastifyApi();
  const { skip, variables } = options;

  // Skip the Apollo branch entirely while REST is active — hooks must still be called.
  const graphqlResult = useQuery(FLIGHT_PROFILE, {
    ...options,
    skip: skip || enableFastifyApi,
  });
  const [restExecute, restState] = useAsyncCall(fetchFullProfile);

  useEffect(() => {
    if (enableFastifyApi && !skip) restExecute(variables);
  }, [enableFastifyApi, skip]);

  const missingReason = missingProfileReason(restState.data);
  const emptyProfile = Boolean(missingReason);
  // Memoized: a fresh Error each render changes identity, and consumers key effects on it.
  const emptyProfileError = useMemo(
    () => (missingReason ? buildEmptyProfileError(missingReason) : undefined),
    [missingReason],
  );

  if (enableFastifyApi) {
    // Apollo clears `data` while skipped (its skip standby result) and Results relies on that:
    // it writes bookingProfileVar in render while `data` is truthy, and the write is what flips
    // `skip` on. A `data` that survives the flip re-engages that write on every render — an
    // infinite re-render loop. Same for the 200-with-null-sections response (no token/session):
    // collapse it to undefined so it reads as "no profile" instead of a truthy-but-empty object.
    //
    // And report it as an error, which is what the GraphQL query did for the same case. Without
    // that this response was neither data nor error, so callers had no terminal state: Results
    // gates its loader on the profile being present and spun forever on a token Manage could not
    // resolve. `data` stays undefined, so the re-render loop above cannot come back.
    return {
      data: skip || emptyProfile ? undefined : restState.data,
      loading: skip ? false : restState.loading,
      error: skip ? undefined : restState.error ?? emptyProfileError,
      refetch: () => restExecute(variables),
    };
  }
  return graphqlResult;
}
